/**
 * THE DEFAULT CONVERSION VALUE, ASKED.
 *
 * What a converted lead is sent as when nothing on the lead says (no deal
 * value, fee or payment). One setting for the workspace, not one per platform
 * (services/conversionDefault on the server). It used to be 35,000 in every
 * sender's code - Measure Marketer Academy's own course fee, in rupees - and
 * went to every workspace's ad accounts as revenue nobody earned.
 *
 * Three places, one write (PUT /api/settings/conversion-default):
 *   ConversionDefaultBanner  top of every page, for an admin, while a lead
 *                            conversion is on and nothing is chosen
 *   ConversionDefaultCard    Settings > General, beside the currency
 *   ConversionDefaultField   Signal catalogue > Events, above the lead events
 *
 * Always an explicit Save. "Send without a value" is a choice of its own,
 * never a silent zero. The amount is in the workspace currency; a workspace
 * that has not confirmed its currency confirms it in the same Save.
 */
import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Target } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CurrencySelect } from '@/components/settings/currency-choice';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { getUserRole } from '@/lib/auth';
import { money } from '@/lib/money';
import { cn, fmtZoned } from '@/lib/utils';

export const EXPLAIN = 'Sent only when a converted lead has no deal value, fee or payment.';

export function useConversionDefault({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['conversion-default'],
    queryFn: async () => (await api.get('/api/settings/conversion-default')).data,
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

function useSaveConversionDefault() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.put('/api/settings/conversion-default', body)).data,
    onSuccess: (data) => {
      qc.setQueryData(['conversion-default'], data);
      qc.invalidateQueries({ queryKey: ['workspace-currency'] });
      qc.invalidateQueries({ queryKey: ['org-settings'] });
      qc.invalidateQueries({ queryKey: ['signal-matrix'] });
    },
  });
}

const isAdmin = () => String(getUserRole() || '').toLowerCase() === 'admin';

/** The default in words: "₹35,000", "Send without a value", or "Not set". */
export function defaultWords(d) {
  if (!d) return '';
  if (d.state === 'value') return money(d.amount, d.currency, { decimals: 0 });
  if (d.state === 'none') return 'Send without a value';
  return 'Not set';
}

/** Why the current state is what it is, when it needs saying. */
function StateNote({ d }) {
  if (!d) return null;
  if (d.state === 'stale') {
    return (
      <p className="text-[13px] leading-relaxed text-amber-800 dark:text-amber-300">
        Your default of {money(d.amount, d.set_in || d.currency, { decimals: 0 })} was set in {d.set_in}, and the workspace is now in {d.workspace?.currency}.
        It is not sent until you confirm a default in {d.workspace?.currency}. Nothing is converted.
      </p>
    );
  }
  if (d.state === 'unset' && d.legacy) {
    return (
      <p className="text-[13px] leading-relaxed text-amber-800 dark:text-amber-300">
        A default of {money(d.legacy.amount, d.legacy.currency || 'INR', { decimals: 0 })} was saved here without anyone choosing it, so it is not sent.
        Until you choose, no value is sent.
      </p>
    );
  }
  if (d.state === 'unset') {
    return (
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        Nothing is chosen yet. A converted lead with no deal value, fee or payment is sent without a value.
      </p>
    );
  }
  return null;
}

/**
 * The editor. Stages a choice; one Save sends it. `compact` lays it out on one
 * line for the banner.
 */
export function ConversionDefaultField({ d, compact = false, onSaved }) {
  const admin = isAdmin();
  const save = useSaveConversionDefault();
  const { toast } = useToast();
  const initialMode = d?.state === 'none' ? 'none' : 'value';
  const initialAmount = d?.state === 'value' ? String(d.amount) : '';
  const wsConfirmed = d?.workspace?.confirmed === true;
  const [mode, setMode] = useState(initialMode);
  const [amount, setAmount] = useState(initialAmount);
  const [cur, setCur] = useState(null);
  // Each field is its own radio group: the banner and a page's own field can
  // be on screen together and must not uncheck each other.
  const group = `cd-mode-${useId()}`;
  useEffect(() => { setMode(initialMode); setAmount(initialAmount); setCur(null); }, [d?.state, d?.amount, d?.confirmed_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return null;
  const guessed = d.workspace?.suggestion?.source && d.workspace.suggestion.source !== 'default' ? d.workspace.suggestion.currency : null;
  const currency = wsConfirmed ? d.workspace.currency : (cur || guessed || null);
  const dirty = mode !== initialMode || (mode === 'value' && amount !== initialAmount) || (!wsConfirmed && cur != null);
  const needsAnswer = !d.chosen;
  const canSave = mode === 'none' || (Number(amount) > 0 && !!currency);

  const submit = async () => {
    try {
      await save.mutateAsync({ mode, amount: mode === 'value' ? Number(amount) : null, currency: wsConfirmed ? null : currency });
      toast({ title: mode === 'value' ? `Default conversion value saved: ${money(Number(amount), currency, { decimals: 0 })}` : 'Saved: lead conversions go without a value when nothing is known', variant: 'success' });
      onSaved?.();
    } catch (err) {
      toast({ title: 'Not saved', description: err.response?.data?.error || err.message, variant: 'destructive' });
    }
  };
  const cancel = () => { setMode(initialMode); setAmount(initialAmount); setCur(null); };

  if (!admin) {
    return (
      <div className="space-y-1">
        <p className="text-[14px]"><span className="font-medium">{defaultWords(d)}</span></p>
        <p className="text-[13px] text-muted-foreground">{EXPLAIN} Only a workspace admin can change it.</p>
      </div>
    );
  }

  return (
    <div className={cn(compact ? 'flex flex-wrap items-center gap-x-4 gap-y-2' : 'space-y-3')} data-testid="conversion-default-field">
      <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', compact && 'contents')}>
        <label className="inline-flex items-center gap-2 text-[14px]">
          <input type="radio" name={group} checked={mode === 'value'} onChange={() => setMode('value')} className="accent-primary" />
          A default value
        </label>
        <span className="inline-flex items-center gap-2">
          <Input type="number" min="0" inputMode="decimal" value={amount} disabled={mode !== 'value' || save.isPending}
            onChange={(e) => { setMode('value'); setAmount(e.target.value); }}
            placeholder="Amount" aria-label="Default conversion value" className={cn('h-9 w-[140px]', compact && 'h-8 bg-card')} />
          {wsConfirmed ? (
            <span className="text-[13.5px] text-muted-foreground" title="Your workspace currency (Settings > General)">{currency}</span>
          ) : (
            <CurrencySelect className={cn('w-[190px]', compact && 'h-8 bg-card')} value={currency} onChange={setCur}
              offered={d.offered} suggested={guessed} disabled={mode !== 'value' || save.isPending} />
          )}
        </span>
        <label className="inline-flex items-center gap-2 text-[14px]">
          <input type="radio" name={group} checked={mode === 'none'} onChange={() => setMode('none')} className="accent-primary" />
          Send without a value
        </label>
      </div>
      {!compact && <p className="text-[13px] text-muted-foreground">{EXPLAIN}{!wsConfirmed && ' Saving also confirms your workspace currency.'}</p>}
      {!compact && <StateNote d={d} />}
      {(dirty || needsAnswer) && (
        <span className="inline-flex items-center gap-2">
          <Button size="sm" className={cn(compact && 'h-8')} onClick={submit} disabled={save.isPending || !canSave}>
            {save.isPending && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
            Save
          </Button>
          {dirty && (
            <Button size="sm" variant="ghost" className={cn(compact && 'h-8')} onClick={cancel} disabled={save.isPending}>Cancel</Button>
          )}
          {dirty && !compact && <span className="text-[13px] text-muted-foreground">Unsaved change</span>}
        </span>
      )}
    </div>
  );
}

/**
 * The one-time question, as a strip above every page: an admin, a lead
 * conversion switched on, and nothing chosen (the server decides, and a store
 * is only asked once it has leads). No dismiss: until it is answered, every
 * converted lead with no deal value goes to the ad platforms without one.
 */
export function ConversionDefaultBanner() {
  const admin = isAdmin();
  const { data } = useConversionDefault({ enabled: admin });
  if (!admin || !data || !data.ask) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b card-attention px-4 py-2 text-[13px] text-amber-900 dark:text-amber-200"
      data-testid="conversion-default-banner">
      <Target className="size-4 shrink-0" />
      <span className="min-w-[16rem] flex-1">
        <span className="font-semibold">Set your default conversion value.</span>{' '}
        Lead conversions are on for {(data.on_labels || []).join(', ')}. {EXPLAIN}{' '}
        {data.state === 'stale'
          ? <>Your old default was set in {data.set_in}; the workspace is now in {data.workspace?.currency}.</>
          : data.legacy
            ? <>The {money(data.legacy.amount, data.legacy.currency || 'INR', { decimals: 0 })} saved earlier was never chosen and is not sent.</>
            : <>Until you choose, they are sent without a value.</>}
      </span>
      <ConversionDefaultField d={data} compact />
    </div>
  );
}

/** Settings > General: the default, beside the currency it is in. */
export function ConversionDefaultCard() {
  const { data, isLoading, isError } = useConversionDefault();
  // A store with no leads has no converted lead to value: no card (the
  // server says so, the same rule as the banner and the gate).
  if (data && !data.concerns && !data.chosen) return null;
  return (
    <Card id="conversion-default" data-testid="conversion-default-card">
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <Target className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Default conversion value</h3>
        </div>
        {isLoading ? (
          <p className="text-[13px] text-muted-foreground">Loading...</p>
        ) : isError || !data ? (
          <p className="text-[13px] text-muted-foreground">Could not read the default conversion value. Reload to try again.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-[14px] leading-relaxed">
              The value a converted lead carries when nothing on it says, for every platform.
            </p>
            <ConversionDefaultField d={data} />
            {data.chosen && data.confirmed_at && (
              <p className="text-[13px] text-muted-foreground">
                Chosen on {fmtZoned(data.confirmed_at, 'd MMM yyyy')}.
                {(data.on_labels || []).length > 0 && <> Used by lead conversions to {data.on_labels.join(', ')}.</>}
              </p>
            )}
            <p className="text-[13px] text-muted-foreground">
              Every delivery shows which value it carried, in the <Link to="/studio?tab=events" className="font-medium text-primary hover:underline">Signal catalogue</Link>, the lead and Conversions.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One line for a place that SHOWS the default without editing it (a
 * destination card, a matrix row): what a converted lead with nothing on it
 * is sent as, and where to change it.
 */
export function ConversionDefaultNote({ className }) {
  const { data } = useConversionDefault();
  if (!data) return null;
  return (
    <p className={cn('text-[13px] text-muted-foreground', className)}>
      Value: the lead&apos;s deal value, fee or payments, else your default ({data.state === 'value'
        ? <span className="font-medium text-foreground">{defaultWords(data)}</span>
        : data.state === 'none' ? 'sent without a value' : <span className="font-medium text-amber-700 dark:text-amber-300">not set, so none is sent</span>}).{' '}
      <Link to="/settings?tab=general#conversion-default" className="font-medium text-primary hover:underline">Change</Link>
    </p>
  );
}
